import { PowerProfileDataPoint, allDays } from 'greener-ui-library';
import { parseTimeToSeconds } from 'pages/Blueprints/utils/calculatePowerProfileStats/calculatePowerProfileStats';
import { aggregateDataByMinute } from './powerRequirementsUtils';

/**
 * Convert PowerProfileDataPoint[] to CSV format expected by backend
 * Format: week_day,time,power_demand_kw
 * week_day: 0-6 (0=Monday, 1=Tuesday, ..., 6=Sunday)
 * time: HH:MM:SS (24h format, 1s intervals)
 * power_demand_kw: float value
 */
export const convertPowerProfileDataToCsv = (dataPoints: PowerProfileDataPoint[]): string => {
  if (!dataPoints || dataPoints.length === 0) {
    return '';
  }

  const csvRows = ['week_day,time,power_demand_kw'];

  dataPoints.forEach((point) => {
    let time = '00:00';

    if (point.timestamp.includes('T')) {
      // ISO format - parse as Date
      const date = new Date(point.timestamp);
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      const seconds = date.getUTCSeconds().toString().padStart(2, '0');
      time = `${hours}:${minutes}:${seconds}`;
    } else if (point.timestamp.includes(':')) {
      // Custom format "dayIndex:time" where time can be HH:MM or HH:MM:SS
      const parts = point.timestamp.split(':');
      // Skip first part (dayIndex), join the rest to get the time
      time = parts.slice(1).join(':');
    }

    // Use dayIndex directly (0-6)
    const weekDay = point.dayIndex;

    // Format energy value
    const energy = point.power.toFixed(2);

    csvRows.push(`${weekDay},${time},${energy}`);
  });

  return csvRows.join('\n');
};

/**
 * Generate power profile data rows for a single day
 * @internal - Used internally by generateManualModeCsv and generateSingleDayCsv
 */
const generateDayRows = (
  dayIndex: number,
  isWorkingDay: boolean,
  workingHoursStart: string,
  workingHoursEnd: string,
  peakPowerKw: number,
  averagePowerKw: number,
  peaksPerDay?: number,
  peaksDuration?: number,
  restDayAveragePowerKw?: number,
  resolutionSeconds: number = 1
): string[] => {
  const rows: string[] = [];

  // Parse working hours
  const startSeconds = parseTimeToSeconds(workingHoursStart)!;
  const endSeconds = parseTimeToSeconds(workingHoursEnd)!;
  const workingDurationSeconds = endSeconds - startSeconds;

  // Calculate peak intervals if peaks are defined
  const peakIntervals: Array<{ start: number; end: number }> = [];
  if (peaksPerDay && peaksPerDay > 0 && peaksDuration && peaksDuration > 0) {
    const peakDurationSeconds = peaksDuration;
    const totalPeakSeconds = peaksPerDay * peakDurationSeconds;

    if (totalPeakSeconds <= workingDurationSeconds) {
      // Calculate spacing between peaks
      const availableSpaceSeconds = workingDurationSeconds - totalPeakSeconds;
      const gapBetweenPeaks = availableSpaceSeconds / (peaksPerDay + 1);

      // Distribute peaks evenly
      for (let i = 0; i < peaksPerDay; i++) {
        const peakStartSeconds = startSeconds + gapBetweenPeaks * (i + 1) + peakDurationSeconds * i;
        const peakEndSeconds = peakStartSeconds + peakDurationSeconds;
        peakIntervals.push({ start: peakStartSeconds, end: peakEndSeconds });
      }
    } else {
      // If total peak duration exceeds working hours, treat entire working period as peak
      peakIntervals.push({ start: startSeconds, end: endSeconds });
    }
  }

  // Helper function to check if current time is within any peak interval
  const isInPeakInterval = (currentSeconds: number): boolean => {
    return peakIntervals.some((interval) => currentSeconds >= interval.start && currentSeconds < interval.end);
  };

  const hasPeakInWindow = (windowStartSeconds: number, windowEndSeconds: number): boolean => {
    if (!peakIntervals.length || windowStartSeconds >= windowEndSeconds) {
      return false;
    }

    return peakIntervals.some((interval) => interval.start < windowEndSeconds && interval.end > windowStartSeconds);
  };

  // Generate data for each configured interval
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 1) {
      for (let second = 0; second < 60; second += resolutionSeconds) {
        const currentSeconds = hour * 3600 + minute * 60 + second;
        const time =
          resolutionSeconds === 1
            ? `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second
                .toString()
                .padStart(2, '0')}`
            : `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

        let power: number;

        if (isWorkingDay) {
          // Working day
          if (currentSeconds >= startSeconds && currentSeconds < endSeconds) {
            // Within working hours
            if (
              peaksPerDay &&
              peaksPerDay > 0 &&
              (resolutionSeconds === 1
                ? isInPeakInterval(currentSeconds)
                : hasPeakInWindow(currentSeconds, currentSeconds + resolutionSeconds))
            ) {
              // During peak period
              power = peakPowerKw;
            } else {
              // During working hours but not peak period
              power = averagePowerKw;
            }
          } else {
            // Outside working hours on working day
            power = restDayAveragePowerKw ?? averagePowerKw;
          }
        } else {
          // Non-working day (rest day)
          power = restDayAveragePowerKw ?? averagePowerKw;
        }

        rows.push(`${dayIndex},${time},${power.toFixed(2)}`);
      }
    }
  }

  return rows;
};

/**
 * Generate minute-resolution CSV for weekly chart display in manual mode.
 * Uses second-based peak windows and marks a minute as peak if any second in that minute overlaps a peak.
 */
export const generateManualModeChartCsv = (
  peakPowerKw: number,
  averagePowerKw: number,
  workingDays: string[],
  workingHoursStart: string,
  workingHoursEnd: string,
  peaksPerDay?: number,
  peaksDuration?: number,
  restDayAveragePowerKw?: number
): string => {
  const csvRows = ['week_day,time,power_demand_kw'];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const dayName = dayNames[dayIndex];
    const isWorkingDay = workingDays.includes(dayName);

    const dayRows = generateDayRows(
      dayIndex,
      isWorkingDay,
      workingHoursStart,
      workingHoursEnd,
      peakPowerKw,
      averagePowerKw,
      peaksPerDay,
      peaksDuration,
      restDayAveragePowerKw,
      60
    );

    csvRows.push(...dayRows);
  }

  return csvRows.join('\n');
};

/**
 * Generate CSV for a single working day
 * Useful for visualizing just the working hours period in charts
 */
export const generateSingleDayCsv = (
  peakPowerKw: number,
  averagePowerKw: number,
  workingHoursStart: string,
  workingHoursEnd: string,
  peaksPerDay?: number,
  peaksDuration?: number
): string => {
  const csvRows = ['week_day,time,power_demand_kw'];

  const dayRows = generateDayRows(
    0, // Use Monday (index 0)
    true, // Always treat as working day
    workingHoursStart,
    workingHoursEnd,
    peakPowerKw,
    averagePowerKw,
    peaksPerDay,
    peaksDuration,
    undefined,
    60
  );

  csvRows.push(...dayRows);
  return csvRows.join('\n');
};

/**
 * Convert CSV back to PowerProfileDataPoint[] for chart display
 * This ensures the chart displays exactly what we're sending to backend
 */
export const convertCsvToPowerProfileData = (csvString: string): PowerProfileDataPoint[] => {
  if (!csvString) {
    return [];
  }

  const lines = csvString.trim().split('\n');
  const dataPoints: PowerProfileDataPoint[] = [];

  // Skip header line
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      continue;
    }

    const [weekDay, time, powerDemandKw] = line.split(',');
    const dayIndex = parseInt(weekDay);
    const day = allDays[dayIndex];
    const [hours] = time.split(':');
    const hour = parseInt(hours);

    // Use custom timestamp format: "dayIndex:HH:MM"
    const timestamp = `${dayIndex}:${time}`;

    dataPoints.push({
      timestamp,
      power: parseFloat(powerDemandKw),
      day,
      hour,
      dayIndex,
    });
  }

  return dataPoints;
};

/**
 * Get maximum power value from CSV string
 * Returns the peak power demand from the power profile CSV
 */
export const getPeakPowerFromCsv = (csvString: string): number | null => {
  if (!csvString) {
    return null;
  }

  const dataPoints = convertCsvToPowerProfileData(csvString);
  if (dataPoints.length === 0) {
    return null;
  }

  const perMinuteDataset = aggregateDataByMinute(dataPoints); // to avoid Maximum call stack size exceeded runtime error

  const maxPower = Math.max(...perMinuteDataset.map((point) => point.power));
  return maxPower;
};
